// AssetsPanel — right rail listing synthesized assets. Two tabs: this thread / all.
// Search filters by title + content. Clicking a card opens the AssetModal viewer.

import { useEffect, useMemo, useState } from 'react';
import * as api from '../api';
import type { AssetType, ThreadAsset } from '../types';

type Tab = 'thread' | 'all';

const ASSET_LABEL: Record<AssetType, string> = {
  brief: 'Briefing',
  timeline: 'Timeline',
  flowchart: 'Flowchart',
  workflow: 'Workflow',
  notes: 'Notes',
  report: 'Report',
  supply_map: 'Supply Map',
};

const ASSET_GLYPH: Record<AssetType, string> = {
  brief: 'B',
  timeline: 'T',
  flowchart: 'F',
  workflow: 'W',
  notes: 'N',
  report: 'R',
  supply_map: 'S',
};

interface Props {
  activeThreadId: string | null;
  refreshKey: number;
  onOpen: (asset: ThreadAsset) => void;
  /** Mobile drawer state — slides in from the right when true. Ignored on lg+. */
  mobileOpen?: boolean;
}

export function AssetsPanel({ activeThreadId, refreshKey, onOpen, mobileOpen = false }: Props) {
  const [tab, setTab] = useState<Tab>('thread');
  const [query, setQuery] = useState('');
  const [assets, setAssets] = useState<ThreadAsset[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fetch =
      tab === 'thread' && activeThreadId
        ? api.listThreadAssets(activeThreadId)
        : api.listAllAssets();
    fetch
      .then((rows) => { if (!cancelled) setAssets(rows); })
      .catch(() => { if (!cancelled) setAssets([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tab, activeThreadId, refreshKey]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return assets;
    return assets.filter((a) =>
      a.title.toLowerCase().includes(q)
      || a.content_markdown.toLowerCase().includes(q)
      || a.type.includes(q),
    );
  }, [assets, query]);

  return (
    <aside
      className={`w-72 shrink-0 border-l border-hairline bg-bg/95 lg:bg-bg/40 flex flex-col z-30
        fixed lg:sticky right-0 top-[57px] bottom-0 lg:bottom-auto lg:h-[calc(100vh-57px)]
        transition-transform duration-200 ease-out
        lg:translate-x-0 ${mobileOpen ? 'translate-x-0 shadow-xl' : 'translate-x-full lg:translate-x-0'}`}
    >
      <div className="px-5 pt-5 pb-3 border-b border-hairline/60">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[13px] font-medium text-ink">Assets</span>
          <span className="text-[11px] text-text3">{filtered.length}</span>
        </div>
        <div className="inline-flex bg-surface border border-hairline rounded-full p-0.5 text-[11px] mb-3">
          <TabButton active={tab === 'thread'} onClick={() => setTab('thread')}>This chat</TabButton>
          <TabButton active={tab === 'all'} onClick={() => setTab('all')}>All</TabButton>
        </div>
        <div className="relative">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search assets…"
            className="w-full bg-card border border-border/70 rounded-md pl-7 pr-3 py-1.5 text-[12px] text-ink placeholder:text-text3 outline-none focus:border-ink/40"
          />
          <svg
            className="absolute left-2 top-1/2 -translate-y-1/2 text-text3"
            width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          >
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3">
        {loading ? (
          <div className="text-[12px] text-text3 px-2 py-4">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState tab={tab} hasQuery={query.trim().length > 0} />
        ) : (
          <ul className="space-y-1.5">
            {filtered.map((a) => (
              <li key={a.id}>
                <button
                  onClick={() => onOpen(a)}
                  className="w-full text-left bg-card border border-border/60 hover:border-ink/30 rounded-md px-3 py-2.5 transition-all hover:-translate-y-px hover:shadow-sm group"
                >
                  <div className="flex items-start gap-2.5">
                    <span className="shrink-0 w-7 h-7 grid place-items-center bg-surface border border-hairline rounded text-[11px] text-text2 font-mono group-hover:text-ink group-hover:border-ink/40 transition-colors">
                      {ASSET_GLYPH[a.type as AssetType] ?? '·'}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] text-ink leading-snug truncate">
                        {a.title}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-text3">
                          {ASSET_LABEL[a.type as AssetType] ?? a.type}
                        </span>
                        <span className="text-[10px] text-text3">·</span>
                        <span className="text-[10px] text-text3">
                          {formatRelative(a.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

function EmptyState({ tab, hasQuery }: { tab: Tab; hasQuery: boolean }) {
  if (hasQuery) {
    return (
      <div className="text-center text-[12px] text-text3 px-3 pt-8 pb-6 leading-relaxed">
        No assets match your search.
      </div>
    );
  }
  return (
    <div className="text-center text-[12px] text-text3 px-3 pt-10 pb-6 leading-relaxed">
      {tab === 'thread'
        ? 'Generate an asset from the Convert menu and it will appear here.'
        : 'No assets yet. Start by converting a chat into a briefing or report.'}
    </div>
  );
}

function TabButton({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full font-medium transition-colors ${
        active ? 'bg-ink text-bg' : 'text-text2 hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

function formatRelative(unixSec: number): string {
  const diffMs = Date.now() - unixSec * 1000;
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const d = new Date(unixSec * 1000);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}
